import {api} from "@/shared/api";

import {BasicDataTable, TypedColumnSettings} from "@/shared/ui/basicDataTable"
import {DriveLogBulkSelector} from "@/features/drivelog/driveLogBulkSelector";
import {RecordingTable, RecordingTableConfig} from "@/shared/recording/recordingTable";
import {showBulkOperationResult} from "@/shared/bulkOperationHelper";
import {isDriveLogDateTimeChangeAllowed} from "@/shared/securityHelper";
import {htmlEscape} from "@/shared/utils/string";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {convertColorIntToHex, DEFAULT_CATEGORY_COLOR} from "@/shared/utils/misc";
import {formatDateTime, parseISOZonedDateTime} from "@/shared/utils/datefunctions";
import {BulkOperationResult, ReportDriveLog, ReportDriveLogCategory} from "@/shared/timr";
import {Platform, RecordedEntryStatus} from "@/shared/timrTypes";

export class DriveLogTable extends RecordingTable<ReportDriveLog, DriveLogBulkSelector> {
	public static readonly RECORDING_DRIVE_LOG_URL = "/timr/recording/getDriveLogs.json";
	public static readonly REPORT_DRIVE_LOG_URL = "/timr/reports/getDriveLogs.json";

	constructor(public config: RecordingTableConfig) {
		super(config);
	}

	/**
	 * If new columns are added, also modify {@link #buildSubTableColumns} to prevent visual errors in the subtable.
	 */
	protected getColumnSettings(): TypedColumnSettings<ReportDriveLog>[] {
		return [
			{
				data: "id",
				orderable: false,
				createdCell: this.renderRowBorder,
				render: BasicDataTable.renderSelectCheckbox,
				visible: this.config.allowSelect,
				width: BasicDataTable.COLUMN_SIZE_CHECKBOX
			},
			{
				data: "id",
				orderable: false,
				createdCell: (this.config.allowSelect ? null : this.renderRowBorder),
				className: "context-menu",
				render: this.contextMenu.render,
				width: BasicDataTable.COLUMN_SIZE_MENU
			},
			{
				data: "userName",
				orderable: this.config.orderable,
				render: BasicDataTable.renderText,
				visible: this.config.showUser
			},
			{data: "plate", orderable: this.config.orderable, render: this.renderCarPlateWithName},
			{
				data: "description",
				orderable: false,
				render: (_data, _type, row) => DriveLogTable.renderDetails(row),
				className: "drive-log-details"
			},
			{
				data: "startPosition",
				orderable: false,
				render: BasicDataTable.renderPositionIcon,
				className: "gps-position gps-position-start no-padding",
				width: BasicDataTable.COLUMN_SIZE_GPS_ICON
			},
			{
				data: "start",
				orderable: this.config.orderable,
				render: BasicDataTable.renderDateTimeWithTimeZone,
				className: "pl-10 pr-10 time-column"
			},
			{data: "durationNet", orderable: false, render: RecordingTable.renderDuration, className: "text-right"},
			{
				data: "startMileage",
				orderable: this.config.orderable,
				render: BasicDataTable.renderNumber,
				className: "text-right"
			},
			{data: "endMileage", orderable: this.config.orderable, render: BasicDataTable.renderNumber, className: "text-right"},
			{data: "distance", orderable: this.config.orderable, render: BasicDataTable.renderNumber, className: "text-right"},
			{
				data: "category",
				orderable: false,
				render: DriveLogTable.renderDriveLogCategory,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			},
			{
				data: "status",
				orderable: false,
				render: RecordingTable.renderStatus,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[6, "desc"]];
	}

	protected delete(recording: ReportDriveLog) {
		super.delete(recording);

		if (recording.status === RecordedEntryStatus.Changeable) {
			BootstrapModal.confirm({
				title: "drivelog.delete.title",
				message: messages.withArguments(
					"drivelog.delete.message",
					recording.carName || "",
					recording.route ? " " + messages.withArguments("drivelog.delete.message.route", recording.route) : "",
					formatDateTime(parseISOZonedDateTime(recording.start))
				),
				yesLabel: "confirmation.delete",
				noLabel: "confirmation.cancel",
				yesAction: (modal: BootstrapModal.Modal, button?: JQuery) => {
					api("recording/deleteDriveLog.json")
						.post({
							id: recording.id
						}).done(() => {
						this.reload(true);
					}).always(() => {
						modal.close();
					});
				}
			});
		}
	}

	protected showRevisions(recording: ReportDriveLog, tr: JQuery) {
		super.showRevisions(recording, tr);

		if (!this.dataTable.row(tr).child.isShown()) {
			api("reports/getDriveLogRevisions.json")
				.get(this.extendAjaxData({id: recording.id}, this.getRoundingFilterParams()))
				.done(driveLogRevisions => {
					this.renderSubTable(driveLogRevisions.data, tr, DriveLogTable.ROW_CLASS_REVISION, (recording) => this.buildRevisionRow(recording));
				});
		} else {
			this.renderSubTable(null, tr, DriveLogTable.ROW_CLASS_REVISION, (recording) => this.buildRevisionRow(recording));
		}
	}

	protected showConflicts(recording: ReportDriveLog, tr: JQuery) {
		super.showConflicts(recording, tr);

		if (!this.dataTable.row(tr).child.isShown()) {
			api("reports/getConflictedDriveLogs.json")
				.get(this.extendAjaxData({id: recording.id}, this.getRoundingFilterParams()))
				.done((data) => {
					this.renderSubTable(data.conflictedDriveLogs, tr, DriveLogTable.ROW_CLASS_CONFLICT, (recording) => this.buildConflictsRow(recording));
				});
		} else {
			this.renderSubTable(null, tr, DriveLogTable.ROW_CLASS_CONFLICT, (recording) => this.buildConflictsRow(recording));
		}

	}

	protected markConflictAsResolved(recording: ReportDriveLog) {
		super.markConflictAsResolved(recording);

		api("reports/markDriveLogConflictAsResolved.json")
			.post({
				id: recording.id
			}).done(() => {
			this.reload();
		});
	}

	protected setStatus(recording: ReportDriveLog, statusNumber: number) {
		super.setStatus(recording, statusNumber);

		api("reports/updateDriveLogStatus.json")
			.post({
				statusNumber: statusNumber,
				idsInclude: recording.id,
				allSelected: false
			}).done((result: BulkOperationResult) => {
			if (!result.allUpdated) {
				showBulkOperationResult(result, "report.setStatus.");
			}
			this.reload();
		});
	}

	protected renderRowBorder(this: void, td: unknown, type: unknown, recording: ReportDriveLog, meta: unknown) {
		if (recording.business) {
			$(td).addClass("border-row-BUSINESS");
		} else {
			$(td).addClass("border-row-PRIVATE");
		}
	}

	public static renderDetails(this: void, recording: ReportDriveLog, subTable = false): string {
		const appendDetails = function (details: string, title: string, value: string): string {
			return value ? (details + `<div><span class="cell-title">${title}: </span>${htmlEscape(value)}</div>`) : details;
		};
		if (recording.startPlatform === Platform.TOUR_MOBILE || recording.endPlatform === Platform.TOUR_MOBILE) {
			const purpose = appendDetails("", messages["report.column.purpose"], recording.purpose);
			const category = recording.category;
			const categoryColor = category?.color ? "#" + convertColorIntToHex(category.color) : DEFAULT_CATEGORY_COLOR;
			const height = subTable ? 44 : 56;
			const venues =
				`<div>
					<div class="sub-column">
						<svg width="14" height="${height}">
							<line x1="7" y1="14" x2="7" y2="${height - 14}" stroke="${categoryColor}" stroke-width="2"></line>
							<line x1="2" y1="19" x2="7" y2="14" stroke="${categoryColor}" stroke-width="2"></line>
							<line x1="7" y1="14" x2="12" y2="19" stroke="${categoryColor}" stroke-width="2"></line>
							<circle cx="7" cy="7" fill="white" r="6" stroke="#777" stroke-width="2"></circle>
							<circle cx="7" cy="${height - 7}" fill="white" r="6" stroke="#777" stroke-width="2"></circle>
						</svg>
					</div>
					<div class="sub-column">
						<p class="start-venue">${htmlEscape(recording.endVenue)}</p>
						<p class="end-venue">${htmlEscape(recording.startVenue)}</p>
					</div>
				</div>`;
			return purpose + venues;
		} else {
			let details = appendDetails("", messages["report.column.visited"], recording.visited);
			details = appendDetails(details, messages["report.column.purpose"], recording.purpose);
			details = appendDetails(details, messages["report.column.route"], recording.route);
			return details;
		}
	}

	public static renderDriveLogCategory(this: void, data: ReportDriveLogCategory, type: string, recording: ReportDriveLog, meta: unknown): string {
		return data?.business ? `<i class="fal fa-dollar-sign" title="${messages["reports.filter.business"]}"></i>` : "";
	}

	protected buildSubTableColumns(recording: ReportDriveLog): JQuery[] {
		return [
			this.config.showUser ? this.buildTextColumn(recording.userName) : null,
			this.buildHtmlColumn(this.renderCarPlateWithName(recording.plate, null, recording, null)),
			this.buildHtmlColumn(DriveLogTable.renderDetails(recording, true), "drive-log-details"),
			this.buildPositionColumn(recording),
			this.buildDateTimeColumn(recording, recording.start),
			this.buildDurationColumn(recording, recording.durationNet),
			this.buildNumberColumn(recording.startMileage),
			this.buildNumberColumn(recording.endMileage),
			this.buildNumberColumn(recording.distance),
			this.buildIconColumn(DriveLogTable.renderDriveLogCategory(recording.category, null, recording, null)),
			this.buildIconColumn(RecordingTable.renderStatus(recording.status, null, recording, null))
		];
	}

	protected createBulkSelector(): DriveLogBulkSelector {
		return new DriveLogBulkSelector({
			dataTable: this.dataTable
		});
	}

	protected updateSelectedRowsCount() {
		const selectedRowsSums = this.bulkSelector.getSums();
		super.updateSelectedRowsCount(" " + messages.withArguments("bulkselection.driveLog.duration",
			selectedRowsSums.distanceSum,
			selectedRowsSums.distanceBusinessSum,
			selectedRowsSums.distancePrivateSum)
		);
	}

	private renderCarPlateWithName(this: void, data: string, type: string, recording: ReportDriveLog, meta: unknown): string {
		const carName = recording.carName;

		if (data === recording.carName) {
			return htmlEscape(data);
		} else {
			return `<div>
						<div>${htmlEscape(data)}</div>
						<div class="car-name">${htmlEscape(carName)}</div>
					</div>`;
		}
	}

	protected renderPopoverContent(recording: ReportDriveLog, rowClass?: string): string {
		let content = super.renderPopoverContent(recording, rowClass);
		if (recording.category && recording.category.name) {
			content += this.renderPopoverContentData(messages["driveLog.category.name"], recording.category.name);
		}
		return content;
	}

	public recordingChangeable(recording: ReportDriveLog): boolean {
		return super.recordingChangeable(recording) && recording.startPlatform !== Platform.TOUR_MOBILE && recording.endPlatform !== Platform.TOUR_MOBILE;
	}

	protected recordingResumable(recording: ReportDriveLog): boolean {
		return super.recordingResumable(recording) && recording.startPlatform !== Platform.TOUR_MOBILE && recording.endPlatform !== Platform.TOUR_MOBILE;
	}

	protected isDateTimeChangeAllowed(anyUser: boolean, userId: number, allowedUserIds: number []): boolean {
		return isDriveLogDateTimeChangeAllowed(anyUser, userId, allowedUserIds);
	}

	protected getRecordingStatus(recording: ReportDriveLog): RecordedEntryStatus {
		return recording.status;
	}

	protected isConflicted(recording: ReportDriveLog): boolean {
		return recording.conflicted;
	}
}
